import { type ReactNode, type ButtonHTMLAttributes } from 'react'

// ─── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>
      {children}
    </div>
  )
}

// ─── Button ───────────────────────────────────────────────────────────────────
type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-gray-900 text-white hover:bg-gray-700',
  secondary: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
  danger: 'bg-red-50 text-red-600 border border-red-100 hover:bg-red-100',
  ghost: 'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  children,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: 'sm' | 'md'
  loading?: boolean
}) {
  const sizeClass = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
  return (
    <button
      {...props}
      disabled={props.disabled ?? loading}
      className={`inline-flex items-center gap-2 font-medium rounded-lg transition-colors
        ${sizeClass} ${VARIANT_CLASSES[variant]} disabled:opacity-50 disabled:cursor-not-allowed
        ${className}`}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  )
}

// ─── Badge ────────────────────────────────────────────────────────────────────
type BadgeColor = 'gray' | 'yellow' | 'blue' | 'purple' | 'green' | 'red' | 'teal'
const BADGE_COLORS: Record<BadgeColor, string> = {
  gray:   'bg-gray-100 text-gray-600',
  yellow: 'bg-yellow-50 text-yellow-700',
  blue:   'bg-blue-50 text-blue-700',
  purple: 'bg-purple-50 text-purple-700',
  green:  'bg-green-50 text-green-700',
  red:    'bg-red-50 text-red-600',
  teal:   'bg-teal-50 text-teal-700',
}

export function Badge({ label, color = 'gray' }: { label: string; color?: BadgeColor }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE_COLORS[color]}`}>
      {label}
    </span>
  )
}

// ─── Input / Label ────────────────────────────────────────────────────────────
export function Field({
  label, error, children,
}: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
        focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent
        disabled:bg-gray-50 disabled:text-gray-400 ${props.className ?? ''}`}
    />
  )
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
        focus:outline-none focus:ring-2 focus:ring-gray-900 ${props.className ?? ''}`}
    />
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({
  title, open, onClose, children,
}: {
  title: string
  open: boolean
  onClose: () => void
  children: ReactNode
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────
export function ConfirmModal({
  open,
  title,
  message,
  warning,
  confirmLabel = 'Sí, eliminar',
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  message: string
  warning?: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4">
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-xl">
              ⚠️
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500 mt-1">{message}</p>
              {warning && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mt-2">
                  {warning}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
            <Button
              variant="danger"
              className="bg-red-600 text-white hover:bg-red-700 border-red-600"
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, emoji }: {
  label: string; value: string | number; sub?: string; emoji: string
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-3xl font-black text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <span className="text-3xl">{emoji}</span>
      </div>
    </Card>
  )
}

// ─── EmptyState ───────────────────────────────────────────────────────────────
export function EmptyState({ emoji, title, sub }: { emoji: string; title: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
      <span className="text-4xl">{emoji}</span>
      <p className="font-semibold text-gray-700">{title}</p>
      {sub && <p className="text-sm text-gray-400">{sub}</p>}
    </div>
  )
}
